import React, { useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import {
  Layout,
  Button,
  Row,
  Col,
  Dropdown,
  Space,
  Divider,
  Table,
  Select,
  Input,
  Form,
  DatePicker,
  MenuProps,
  Modal,
  Image,
  Switch,
  Card,
} from "antd";
import {
  PlusOutlined,
  CheckOutlined,
  EditOutlined,
  SaveOutlined,
  MoreOutlined,
  DeleteOutlined,
  UserAddOutlined,
  EllipsisOutlined,
  ControlFilled,
} from "@ant-design/icons";
import "./SaleForm.css";
import MaterialForm from "../MaterialForm/MaterialForm";
import NewBuyerForm from "../NewBuyerForm/NewBuyerForm";
import moment from "moment";
import TextArea from "antd/es/input/TextArea";
import SelectBuyerForm from "../SelectBuyerForm/SelectBuyerForm";
import MaterialSummary from "../MaterialSummary/MaterialSummary";

export interface SaleFormProps {
  scaleSerialPort: any;
  buyers: any[];
  selectedBuyerInfo: any;
  saleInfo: any;
  postBuyerSelected: (buyerID: string, dateString: string) => void;
  recordMaterial: () => any;
  captureScaleImage: () => any;
  captureMaterialImage: () => any;
  postAddToSale: (materialInfo: any) => void;
  putMaterial: (materialInfo: any) => any;
  postCompleteSale: (materialsList: any[], notes: string, totalNetWeight: Number, totalNumMaterials: Number) => any;
  getSale: (saleIDFromURL: string) => any;
  getMaterial: (materialID: string) => any;
  updateLoading: (loading: boolean) => void;
  resetNewSalePage: () => void;
  postBuyer: (buyerInfo: any) => void;
  postMetal: (metalInfo: any) => void;
  deleteMaterial: (materialID: string) => void;
  getMetals: () => any;
  getCategories: () => any;
  resetImageView: () => void;
  getSelectedBuyerInfo: (buyerID: string) => void;
  showSelectBuyerForm: boolean;
  scaleImageSrc: string;
  materialImageSrc: string;
  updateSaleDate: (date: string) => void;
}

const SaleForm = (props: SaleFormProps) => {
  const [materialsList, setMaterialsList] = useState([]) as any;
  const [totalNetWeight, setTotalNetWeight] = useState(0.0) as any;
  const [totalNumMaterials, setTotalNumMaterials] = useState(0) as any;
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  // const [buyerConfirmed, setBuyerConfirmed] = useState(false);
  const [saleValid, setSaleValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saleIDFromURL, setSaleIDFromURL] = useState("");
  const [editMaterialData, setEditMaterialData] = useState(null) as any;
  const [tableFormMessage, setTableFormMessage] = useState("");
  const [showNewBuyerForm, setShowNewBuyerForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [loadedSale, setLoadedSale] = useState({}) as any;
  const [ableToEditSale, setAbleToEditSale] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState({}) as any;
  const [metals, setMetals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [notes, setNotes] = useState("");
  const [showSummaryView, setShowSummaryView] = useState(false);
  const [isDateChanged, setIsDateChanged] = useState(false);
  const [dateSelectedFormStatus, setDateSelectedFormStatus] = useState("") as any;
  const [dateSelectedFormMessage, setDateSelectedFormMessage] = useState("");
  const weightRegEx = /^\d+\.\d{1}$/;

  const location = useLocation();

  const newSaleColumns = [
    {
      title: "Item Number",
      key: "index",
      render: (text: any, record: any, index: any) => index + 1,
      align: "center" as const,
    },
    {
      title: "Images",
      key: "index",
      render: (record: any) => (
        <>
          <Image
            height={50}
            width={50}
            src={record.scaleImageSrc || record.scaleImageURL || record.imageURL}
            wrapperStyle={{ marginRight: "10px" }}
            preview={{
              toolbarRender: () => (
                <>
                  <h1 style={{ color: "#000000" }}>{record.displayName}</h1>
                  <h3 style={{ color: "#000000" }}>Raw Weight: {Number(record.rawWeight).toFixed(1)} kg</h3>
                  <h3 style={{ color: "#000000" }}>Net Weight: {Number(record.netWeight).toFixed(1)} kg</h3>
                </>
              ),
            }}
          />
          <Image
            height={50}
            width={50}
            src={record.materialImageSrc || record.materialImageURL}
            preview={{
              toolbarRender: () => (
                <>
                  <h1 style={{ color: "#000000" }}>{record.displayName}</h1>
                  <h3 style={{ color: "#000000" }}>Raw Weight: {Number(record.rawWeight).toFixed(1)} kg</h3>
                  <h3 style={{ color: "#000000" }}>Net Weight: {Number(record.netWeight).toFixed(1)} kg</h3>
                </>
              ),
            }}
          />
        </>
      ),
      align: "center" as const,
    },
    {
      title: "Material Name",
      key: "materialName",
      dataIndex: "displayName",
      align: "left" as const,
    },
    {
      title: "Raw Weight (kg)",
      key: "rawWeight",
      dataIndex: "rawWeight",
      align: "center" as const,
      render: (record: any) => Number(record).toFixed(1),
    },
    {
      title: "Waste Weight (kg)",
      key: "wasteWeight",
      align: "center" as const,
      render: (record: any) => {
        let wasteInfo = "(";
        if (record.smallBag > 0) {
          if (record.bigBag > 0) wasteInfo += `${record.smallBag} small, `;
          else wasteInfo += `${record.smallBag} small`;
        }
        if (record.bigBag > 0) {
          if (record.jumboBag > 0) wasteInfo += `${record.bigBag} big, `;
          else wasteInfo += `${record.bigBag} big`;
        }
        if (record.jumboBag > 0) {
          wasteInfo += `${record.jumboBag} jumbo`;
        }
        wasteInfo += ")";
        return (
          <>
            <Row justify="center">
              {!isNewSaleOrNotCompleted() ? (
                <Row justify="center">{Number(record.wasteWeight).toFixed(1)}</Row>
              ) : (
                <Input
                  placeholder="0.0"
                  style={{
                    width: "150px",
                    marginBottom: "10px",
                    marginRight: "5px",
                    textAlign: "center",
                  }}
                  value={record.wasteWeight}
                  onClick={(e) =>
                    e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length)
                  }
                  onChange={(e) => updateWasteWeight(record, e.target.value)}
                />
              )}
            </Row>

            {(record.smallBag > 0 || record.bigBag > 0 || record.jumboBag > 0) && (
              <Row justify="center" style={{ fontSize: "12px" }}>
                {wasteInfo}
              </Row>
            )}
            {record.other && (
              <Row justify="center" style={{ fontSize: "12px" }}>
                ( Other: {record.other})
              </Row>
            )}
          </>
        );
      },
    },
    {
      title: "Net Weight (kg)",
      key: "netWeight",
      dataIndex: "netWeight",
      align: "center" as const,
      render: (record: any) => Number(record).toFixed(1),
    },
    {
      title: "Manual Entry",
      key: "manualEntry",
      dataIndex: "manualEntry",
      align: "center" as const,
      render: (record: any) =>
        record.manualEntry ? (
          <>
            <ControlFilled style={{ fontSize: "20px", color: "orange", marginRight: "5px" }} />
            Yes
          </>
        ) : (
          <>
            <ControlFilled style={{ fontSize: "20px", color: "green", marginRight: "5px" }} />
            No
          </>
        ),
    },
    {
      title: "",
      key: "editMaterial",
      align: "center" as const,
      render: (record: any) =>
        record.updateWasteWeight ? (
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => {
              saveWasteWeight(record);
            }}
          >
            Save
          </Button>
        ) : null,
    },
    {
      title: "",
      key: "options",
      align: "center" as const,
      width: "7.5%",
      render: (record: any) => {
        const items: MenuProps["items"] = [
          { key: "edit", label: "Edit Material", icon: <EditOutlined disabled={!ableToAddMaterials()} /> },
          { key: "delete", label: "Delete Material", icon: <DeleteOutlined />, style: { color: "red" } },
        ];
        return ableToAddMaterials() ? (
          <Dropdown
            menu={{
              items,
              onClick: ({ key }) => {
                if (key === "edit") {
                  openMaterialForm();
                  setEditMaterialData({
                    materialID: record.materialID,
                    name: record.name,
                    rawWeight: record.rawWeight,
                    wasteWeight: record.wasteWeight,
                    netWeight: record.netWeight,
                    manualEntry: record.manualEntry,
                    smallBag: record.smallBag,
                    bigBag: record.bigBag,
                    jumboBag: record.jumboBag,
                    other: record.other,
                    displayName: record.displayName,
                    metalID: record.metalID,
                    notes: record.notes,
                  });
                } else if (key === "delete") {
                  openDeleteSaleModal(record);
                }
              },
            }}
            trigger={["click"]}
          >
            <a onClick={(e) => e.preventDefault()}>
              <MoreOutlined style={{ fontSize: "24px" }} />
            </a>
          </Dropdown>
        ) : (
          <></>
        );
      },
    },
  ];

  useEffect(() => {
    getSaleInfo();
  }, [props.buyers, saleIDFromURL, location.pathname]);

  const getSaleInfo = () => {
    const IDFromURL = location.pathname.split("/")[2];
    setSaleIDFromURL(IDFromURL);
    getMetalsForm();
    if (IDFromURL !== undefined) loadSaleInfo(IDFromURL);
    else resetSaleInfo();
  };

  const isNewSaleOrNotCompleted = () => {
    return location.pathname.split("/")[2] === undefined || !loadedSale.completed;
  };

  const items: MenuProps["items"] = [
    { key: "edit", label: "Edit Material", icon: <EditOutlined disabled={!isNewSaleOrNotCompleted()} /> },
    { key: "delete", label: "Delete Material", icon: <DeleteOutlined />, style: { color: "red" } },
  ];

  const isTimestampMoreThan2Hours = () => {
    if (loadedSale.createdTimestamp !== undefined) {
      const createdMilliseconds = new Date(loadedSale.createdTimestamp).getTime();
      const currentTimeMilliseconds = new Date().getTime();
      // return currentTimeMilliseconds - createdMilliseconds > 1814000000 ? true : false;
      return currentTimeMilliseconds - createdMilliseconds > 7776000000 ? true : false;
    }
    return false;
  };

  const validateForm = () => {
    let validationFormStatus = true;
    materialsList.forEach((material: any) => {
      if (material.updateWasteWeight) validationFormStatus = false;
    });
    if (!validationFormStatus) setTableFormMessage("Save all changes for waste weight");
    return validationFormStatus;
  };

  const validateWasteWeight = (record: any) => {
    let validationWasteWeightStatus = true;
    if (!weightRegEx.test(record.wasteWeight)) {
      validationWasteWeightStatus = false;
      let currMaterialsList = [...materialsList];
      let currMaterial = currMaterialsList.find((material: any) => material.materialID === record.materialID);
      currMaterial.wasteWeightFormStatus = "error";
      currMaterial.wasteWeightFormMessage = "Weight must have 1 decimal place. Example 0.0 kg";
      setMaterialsList(currMaterialsList);
    }
    return validationWasteWeightStatus;
  };

  const validateDate = () => {
    let validationDateStatus = true;
    if (isDateChanged && selectedDate === "") {
      validationDateStatus = false;
      setDateSelectedFormStatus("error");
      setDateSelectedFormMessage("Select a date");
    }
    return validationDateStatus;
  };

  const openDeleteSaleModal = (record: any) => {
    setMaterialToDelete(record);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteSale = () => {
    props.deleteMaterial(materialToDelete.materialID);
    //check if deleted from status
    let currMaterialsList = [...materialsList];
    currMaterialsList = currMaterialsList.filter(
      (material: any) => material.materialID !== materialToDelete.materialID
    );
    calcNumMaterials(currMaterialsList);
    calcTotalNetWeight(currMaterialsList);
    setMaterialsList(currMaterialsList);
    setIsDeleteModalOpen(false);
  };

  const declineDeleteSale = () => {
    setMaterialToDelete({});
    setIsDeleteModalOpen(false);
  };

  const saveWasteWeight = async (record: any) => {
    await props.putMaterial(record);
    const materials = [...materialsList];
    const material = materials.find((material: any) => material.materialID === record.materialID);
    material.updateWasteWeight = false;
    setTableFormMessage("");
    setMaterialsList(materials);
  };

  // const updateWasteWeight = (record: any, wasteWeight: string) => {
  //   setTableFormMessage("");
  //   const materials = [...materialsList];
  //   const material = materials.find((material: any) => material.materialID === record.materialID);
  //   material.wasteWeight = wasteWeight;
  //   material.updateWasteWeight = true;
  //   material.netWeight = Number(material.rawWeight) - Number(wasteWeight);
  //   setMaterialsList(materials);
  // };

  const getMetalsForm = async () => {
    const data = await props.getMetals();
    // const categoriesData = await props.getCategories();
    setMetals(data);
    // setCategories(categoriesData);
  };

  const resetSaleInfo = () => {
    // setSelectedBuyer("");
    setSaleIDFromURL("");
    // setBuyerConfirmed(false);
    setSaleValid(false);
    setMaterialsList([]);
    props.resetNewSalePage();
  };

  const resetEditMaterialData = () => {
    setEditMaterialData(null);
  };

  const loadSaleInfo = async (saleIDFromURL: string) => {
    props.updateLoading(true);
    const saleInfo: any = await props.getSale(saleIDFromURL);
    setLoadedSale(saleInfo);
    props.getSelectedBuyerInfo(saleInfo.buyerID);
    // setSelectedBuyer(saleInfo.buyerID);
    // setBuyerConfirmed(true);
    setSaleValid(true);
    setNotes(saleInfo.notes);
    let currMaterials: any[] = [];
    for (let materialID of saleInfo.materialIDList) {
      const resMaterial = await props.getMaterial(materialID);
      const currMetal: any = metals.find((metal: any) => metal.metalID === resMaterial.metalID);
      resMaterial.key = resMaterial.materialID;
      resMaterial.displayName = currMetal ? currMetal.displayName : "";
      currMaterials.push(resMaterial);
    }
    // currMaterials.sort((a, b) => a.displayName.localeCompare(b.displayName));
    setMaterialsList(currMaterials);
    calcTotalNetWeight(currMaterials);
    calcNumMaterials(currMaterials);
    isAbleToEditSale(saleInfo.completed);
    props.updateLoading(false);
  };

  const isAbleToEditSale = (completed: boolean) => {
    setAbleToEditSale(completed);
  };

  const openMaterialForm = () => {
    setShowMaterialForm(true);
  };

  const closeMaterialForm = () => {
    setShowMaterialForm(false);
  };

  const openSummaryView = () => {
    setShowSummaryView(true);
  };

  const closeSummaryView = () => {
    setShowSummaryView(false);
  };

  const completeSale = () => {
    props.postCompleteSale(materialsList, notes, totalNetWeight, totalNumMaterials);
  };

  const addToMaterialsList = async (materialInfo: any) => {
    let materials = [...materialsList];

    materialInfo.scaleImageSrc = props.scaleImageSrc;
    materialInfo.materialImageSrc = props.materialImageSrc;
    materialInfo.key = materialInfo.materialID;
    materialInfo.displayName = materialInfo.displayName;
    const resData: any = await props.postAddToSale(materialInfo);
    materials.push(materialInfo);
    // materials.sort((a, b) => a.displayName.localeCompare(b.displayName));
    setMaterialsList(materials);
    isSaleValid(materials);
    resetEditMaterialData();
    calcNumMaterials(materials);
    calcTotalNetWeight(materials);
    closeMaterialForm();
  };

  const calcNumMaterials = (materials: any[]) => {
    let currMetals: string[] = [];
    let currNumMaterials = 0;
    materials.forEach((material: any) => {
      if (!currMetals.includes(material.metalID)) {
        currMetals.push(material.metalID);
        currNumMaterials += 1;
      }
    });
    setTotalNumMaterials(currNumMaterials);
  };

  const calcTotalNetWeight = (materials: any[]) => {
    let totalNetWeight = 0.0;
    materials.forEach((material: any) => {
      totalNetWeight = Number(totalNetWeight) + Number(material.netWeight);
    });
    setTotalNetWeight(Number(totalNetWeight).toFixed(1));
  };

  const saveToMaterialsList = async (materialInfo: any) => {
    let materials = [...materialsList];
    materials.forEach((material: any) => {
      if (material.materialID === materialInfo.materialID) {
        material.name = materialInfo.name;
        material.displayName = materialInfo.displayName;
        material.metalID = materialInfo.metalID;
        material.rawWeight = materialInfo.rawWeight;
        material.wasteWeight = materialInfo.wasteWeight;
        material.netWeight = materialInfo.netWeight;
        material.manualEntry = materialInfo.manualEntry;
        material.smallBag = materialInfo.smallBag;
        material.bigBag = materialInfo.bigBag;
        material.jumboBag = materialInfo.jumboBag;
        material.notes = materialInfo.notes;
        material.scaleImageSrc = props.scaleImageSrc;
        material.materialImageSrc = props.materialImageSrc;
      }
    });
    isSaleValid(materials);
    resetEditMaterialData();
    const res = await props.putMaterial(materialInfo);
    calcNumMaterials(materials);
    calcTotalNetWeight(materials);
    setMaterialsList([...materials]);
    closeMaterialForm();
  };

  const isSaleValid = (materials: any) => {
    setSaleValid(materials.length > 0);
  };

  // const isBuyerConfirmed = () => {
  //   console.log("buyerConfirmed", buyerConfirmed);
  //   return buyerConfirmed;
  // };

  const openNewBuyerForm = () => {
    setShowNewBuyerForm(true);
  };

  const closeNewBuyerForm = () => {
    setShowNewBuyerForm(false);
  };

  // const getUpdatedDate = () => {
  //   const dateProperties = selectedDate.split("/");
  //   return new Date(`${dateProperties[2]}-${dateProperties[1]}-${dateProperties[0]}`).toLocaleDateString("en-GB");
  // };

  const ableToAddMaterials = () => {
    // return (!isTimestampMoreThan2Hours() || isNewSaleOrNotCompleted()) && isBuyerConfirmed();
    return !isTimestampMoreThan2Hours() || isNewSaleOrNotCompleted();
  };

  // const setSmallTransation = () => {
  //   if (isDateChanged && selectedDate === "") {
  //     setDateSelectedFormStatus("error");
  //     setDateSelectedFormMessage("Select a date");
  //     return;
  //   }
  //   const smallTransactionBuyer = props.buyers.find((buyer: any) => buyer.name === "Small Transaction");
  //   setSelectedBuyer(smallTransactionBuyer.buyerID);
  //   props.postBuyerSelected(smallTransactionBuyer.buyerID, selectedDate);
  //   setBuyerConfirmed(true);
  //   setIsDateChanged(false);
  // };

  const updateNotes = (text: string) => {
    setNotes(text);
  };

  const getBagInfo = (material: any) => {
    // console.log("here", material);
    let bagInfo = "(";
    if (material.smallBag > 0) {
      if (material.bigBag > 0) bagInfo += `${material.smallBag}S,`;
      else bagInfo += `${material.smallBag}S`;
    }
    if (material.bigBag > 0) {
      if (material.jumboBag > 0) bagInfo += `${material.bigBag}B,`;
      else bagInfo += `${material.bigBag}B`;
    }
    if (material.jumboBag > 0) {
      bagInfo += `${material.jumboBag}J`;
    }
    bagInfo += ")";
    if (material.smallBag == 0 && material.bigBag == 0 && material.jumboBag == 0) bagInfo = "";
    return bagInfo;
  };

  const formatIdentificationCardNumber = (identificationCardNumber: string) => {
    return `${identificationCardNumber.slice(0, 6)}-${identificationCardNumber.slice(
      6,
      8
    )}-${identificationCardNumber.slice(8, 12)}`;
  };

  const updateWasteWeight = (record: any, wasteWeight: string) => {
    record.wasteWeightFormStatus = "";
    record.wasteWeightFormMessage = "";
    setTableFormMessage("");
    const materials = [...materialsList];
    const material = materials.find((material: any) => material.materialID === record.materialID);
    material.wasteWeight = wasteWeight;
    material.updateWasteWeight = true;
    material.netWeight = Number(material.rawWeight) - Number(wasteWeight);
    setMaterialsList(materials);
  };

  const isScaleImageVisible = (material: any) => {
    return (
      (material.scaleImageSrc !== undefined && material.scaleImageSrc !== "" && material.scaleImageSrc !== null) ||
      (material.scaleImageURL !== undefined && material.scaleImageURL !== "" && material.scaleImageURL !== null)
    );
  };

  const isMaterialImageVisible = (material: any) => {
    return (
      (material.materialImageSrc !== undefined &&
        material.materialImageSrc !== "" &&
        material.materialImageSrc !== null) ||
      (material.materialImageURL !== undefined &&
        material.materialImageURL !== "" &&
        material.materialImageURL !== null)
    );
  };

  const updateSetIsDateChanged = () => {
    setIsDateChanged(true);
  };

  const updateSaleDateString = (dateString: string) => {
    setDateSelectedFormStatus("");
    setDateSelectedFormMessage("");
    setSelectedDate(dateString);
  };

  const updateSaleDate = async () => {
    props.updateLoading(true);
    props.updateSaleDate(selectedDate);
    props.updateLoading(false);
  };

  return (
    <div className="sale-form-container">
      <Row>
        <Col span={12}>
          <Row justify="start">
            <h2>New Sale</h2>
          </Row>
        </Col>
        <Col span={12}>
          <Row justify="end">
            {isDateChanged ? (
              <Form layout="vertical">
                <Form.Item validateStatus={dateSelectedFormStatus} help={dateSelectedFormMessage}>
                  <DatePicker
                    allowClear={false}
                    onChange={(date, dateString) => updateSaleDateString(dateString)}
                    format={"DD/MM/YYYY"}
                    showToday
                    disabledDate={(current) => {
                      let customDate = moment().add(1, "day").format("YYYY-MM-DD");
                      return current && current > moment(customDate, "YYYY-MM-DD");
                    }}
                  />
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => validateDate() && updateSaleDate()}
                    style={{ marginLeft: "10px" }}
                  >
                    Update Date
                  </Button>
                </Form.Item>
              </Form>
            ) : (
              <>
                {loadedSale !== undefined && loadedSale.createdTimestamp !== undefined ? (
                  <h2>{new Date(loadedSale.createdTimestamp).toLocaleDateString("en-GB")}</h2>
                ) : (
                  <h2>{new Date(props.saleInfo.createdTimestamp).toLocaleDateString("en-GB")}</h2>
                )}
                <Button
                  type="default"
                  icon={<EditOutlined />}
                  onClick={() => updateSetIsDateChanged()}
                  style={{ marginLeft: "10px" }}
                ></Button>
              </>
            )}
          </Row>
        </Col>
      </Row>
      <Row>
        <Divider style={{ margin: "6px 0" }} />
      </Row>
      <Row style={{ marginBottom: "10px" }}>
        <Col span={12}>
          <Row justify="start">
            <h3>Buyer Information</h3>
          </Row>
        </Col>
        <Col span={12}>
          <Row justify="end"></Row>
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          {props.selectedBuyerInfo.name && (
            <Row justify="start">
              <h3>Name: {props.selectedBuyerInfo.name}</h3>
            </Row>
          )}
        </Col>
        <Col span={12}>
          <Row justify="end">
            <h3>
              IC Number:{" "}
              {props.selectedBuyerInfo.identificationCardNumber
                ? formatIdentificationCardNumber(props.selectedBuyerInfo.identificationCardNumber)
                : " N/A"}
            </h3>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <Row justify="start">
            <h3>Company: {props.selectedBuyerInfo.companyName ? props.selectedBuyerInfo.companyName : " N/A"}</h3>
          </Row>
        </Col>
        <Col span={12}>
          <Row justify="end">
            <h3>
              Passport Number:{" "}
              {props.selectedBuyerInfo.passportNumber ? props.selectedBuyerInfo.passportNumber : " N/A"}
            </h3>
          </Row>
        </Col>
      </Row>
      <Row>
        <Divider style={{ margin: "12px 0" }} />
      </Row>
      <Row>
        <Col span={8}>
          <Row justify="start">
            <h3>
              {totalNumMaterials} Materials ({materialsList.length} Weights)
            </h3>
          </Row>
        </Col>
        <Col span={8}>
          <Row justify="center">
            <h3>Total Net Weight: {Number(totalNetWeight).toFixed(1)} kg</h3>
          </Row>
        </Col>
        <Col span={8}>
          <Row justify="end">
            <Button type="default" onClick={openSummaryView}>
              Sale Summary
            </Button>
          </Row>
        </Col>
      </Row>
      <Row justify="start" className="materials-table">
        <Col span={24}>
          <Row justify="start">
            <Col span={4}>
              <Card className="materials-card" bodyStyle={{ padding: 0 }}>
                <Row justify="center" align="middle" style={{ height: "250px" }}>
                  <Button
                    type="primary"
                    className="add-materials-button"
                    onClick={openMaterialForm}
                    disabled={!ableToAddMaterials()}
                  >
                    <PlusOutlined style={{ fontSize: 30 }} />
                    <Row>Add Material</Row>
                  </Button>
                </Row>
              </Card>
            </Col>
            {materialsList.map((material: any, index: number) => {
              return (
                <Col span={4}>
                  <Card className="materials-card" bodyStyle={{ padding: 0 }}>
                    <Row style={{ height: "250px" }}>
                      <Col span={24}>
                        <Row justify="start">
                          <Col span={20}>
                            <h3>
                              <b>
                                {index + 1}. {material.displayName}
                              </b>
                            </h3>
                          </Col>
                          <Col span={4}>
                            <Row justify="end">
                              <Dropdown
                                menu={{
                                  items,
                                  onClick: ({ key }) => {
                                    if (key === "edit") {
                                      openMaterialForm();
                                      setEditMaterialData({
                                        materialID: material.materialID,
                                        name: material.name,
                                        rawWeight: material.rawWeight,
                                        wasteWeight: material.wasteWeight,
                                        netWeight: material.netWeight,
                                        manualEntry: material.manualEntry,
                                        smallBag: material.smallBag,
                                        bigBag: material.bigBag,
                                        jumboBag: material.jumboBag,
                                        other: material.other,
                                        displayName: material.displayName,
                                        metalID: material.metalID,
                                        notes: material.notes,
                                        scaleImageSrc: material.scaleImageSrc,
                                        materialImageSrc: material.materialImageSrc,
                                      });
                                    } else if (key === "delete") {
                                      openDeleteSaleModal(material);
                                    }
                                  },
                                }}
                                trigger={["click"]}
                              >
                                <a onClick={(e) => e.preventDefault()}>
                                  <EllipsisOutlined style={{ fontSize: "24px" }} />
                                </a>
                              </Dropdown>
                            </Row>
                          </Col>
                        </Row>
                        <Row>
                          <Col span={5}>
                            <h3>Raw:</h3>
                          </Col>
                          <Col span={6}>
                            <Row justify="end">
                              <h3>{Number(material.rawWeight).toFixed(1)}</h3>
                            </Row>
                          </Col>
                          <Col span={3}>
                            <Row justify="end">
                              <h3>kg</h3>
                            </Row>
                          </Col>
                          <Col span={10}>
                            <Row justify="center">
                              <h3>{getBagInfo(material)}</h3>
                            </Row>
                          </Col>
                        </Row>
                        <Row>
                          <Col span={ableToAddMaterials() ? 8 : 5}>
                            <h3>Waste:</h3>
                          </Col>
                          <Col span={ableToAddMaterials() ? 9 : 6}>
                            <Row justify="end">
                              {ableToAddMaterials() ? (
                                <Form.Item
                                  label={<label className="material-form-label">Waste Weight*</label>}
                                  validateStatus={material.wasteWeightFormStatus}
                                  help={material.wasteWeightFormMessage}
                                >
                                  <Input
                                    placeholder="0.0"
                                    style={{
                                      textAlign: "center",
                                    }}
                                    value={material.wasteWeight}
                                    onChange={(e) => updateWasteWeight(material, e.target.value)}
                                  />
                                </Form.Item>
                              ) : (
                                <h3>{Number(material.wasteWeight).toFixed(1)}</h3>
                              )}
                            </Row>
                          </Col>
                          <Col span={6}>
                            <Row justify={ableToAddMaterials() ? "start" : "end"}>
                              <h3>kg (-)</h3>
                            </Row>
                          </Col>
                        </Row>
                        <Row>
                          <Divider style={{ margin: "5px 0", backgroundColor: "black" }} />
                        </Row>
                        <Row>
                          <Col span={5}>
                            <h3>
                              <b>Net:</b>
                            </h3>
                          </Col>
                          <Col span={6}>
                            <Row justify="end">
                              <h3>
                                <b>{Number(material.netWeight).toFixed(1)}</b>
                              </h3>
                            </Row>
                          </Col>
                          <Col span={3}>
                            <Row justify="end">
                              <h3>
                                <b>kg</b>
                              </h3>
                            </Row>
                          </Col>
                        </Row>
                        <Row>
                          {isScaleImageVisible(material) && (
                            <Col span={12}>
                              <Row justify="center">
                                <Image
                                  height={60}
                                  width={70}
                                  src={material.scaleImageSrc || material.scaleImageURL}
                                  preview={{
                                    toolbarRender: () => (
                                      <>
                                        <h1 style={{ color: "#000000" }}>{material.displayName}</h1>
                                        <h3 style={{ color: "#000000" }}>
                                          Raw Weight: {Number(material.rawWeight).toFixed(1)} kg
                                        </h3>
                                      </>
                                    ),
                                  }}
                                />
                              </Row>
                            </Col>
                          )}
                          {isMaterialImageVisible(material) && (
                            <Col span={12}>
                              <Row justify="center">
                                <Image
                                  height={60}
                                  width={70}
                                  src={material.materialImageSrc || material.materialImageURL}
                                  preview={{
                                    toolbarRender: () => (
                                      <>
                                        <h1 style={{ color: "#000000" }}>{material.displayName}</h1>
                                        <h3 style={{ color: "#000000" }}>
                                          Raw Weight: {Number(material.rawWeight).toFixed(1)} kg
                                        </h3>
                                      </>
                                    ),
                                  }}
                                />
                              </Row>
                            </Col>
                          )}
                        </Row>
                        {material.updateWasteWeight && (
                          <Row>
                            <Col span={24}>
                              <Button
                                type="primary"
                                className="save-material-button"
                                icon={<SaveOutlined />}
                                onClick={() => {
                                  validateWasteWeight(material) && saveWasteWeight(material);
                                }}
                              >
                                Save Waste Weight
                              </Button>
                            </Col>
                          </Row>
                        )}
                      </Col>
                    </Row>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Col>
      </Row>
      <Row>
        <Divider style={{ margin: "12px 0" }} />
      </Row>
      <Row>
        <Col span={24}>
          <Row className="material-form-label" style={{ margin: "10px 0" }}>
            Notes
          </Row>
        </Col>
      </Row>
      <Row>
        <Col span={18}>
          <Row justify="start">
            <TextArea
              showCount
              style={{ height: 50 }}
              onChange={(e) => updateNotes(e.target.value)}
              placeholder="Enter sale notes"
              value={notes}
            />
          </Row>
        </Col>
        <Col span={6}>
          <Row justify="end">
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => validateForm() && completeSale()}
              disabled={!saleValid || !ableToAddMaterials()}
              style={{ height: "50px", width: "90%" }}
            >
              Complete Sale
            </Button>
          </Row>
          <Row justify="center" className="table-form-message">
            {tableFormMessage}
          </Row>
        </Col>
      </Row>
      <MaterialForm
        type="sale"
        scaleSerialPort={props.scaleSerialPort}
        showForm={showMaterialForm}
        closeForm={closeMaterialForm}
        recordMaterial={props.recordMaterial}
        captureScaleImage={props.captureScaleImage}
        captureMaterialImage={props.captureMaterialImage}
        addToMaterialsList={addToMaterialsList}
        saveToMaterialsList={saveToMaterialsList}
        editMaterialData={editMaterialData}
        resetEditMaterialData={resetEditMaterialData}
        postMetal={props.postMetal}
        metals={metals}
        categories={categories}
        getMetalsForm={getMetalsForm}
        resetImageView={props.resetImageView}
        scaleImageSrc={props.scaleImageSrc}
        materialImageSrc={props.materialImageSrc}
      />
      <NewBuyerForm
        showForm={showNewBuyerForm}
        closeForm={closeNewBuyerForm}
        editBuyerData={null}
        postBuyer={props.postBuyer}
      />
      <SelectBuyerForm
        showForm={props.showSelectBuyerForm}
        editBuyerData={null}
        postBuyer={props.postBuyer}
        postBuyerSelected={props.postBuyerSelected}
        buyers={props.buyers}
        openNewBuyerForm={openNewBuyerForm}
      />
      <MaterialSummary
        showSummary={showSummaryView}
        hideSummary={closeSummaryView}
        materialsList={[...materialsList]}
        getBagInfo={getBagInfo}
      />
      <Modal
        title="Delete Material"
        open={isDeleteModalOpen}
        okText="Confirm Delete"
        okType="danger"
        onOk={confirmDeleteSale}
        onCancel={declineDeleteSale}
      >
        <p>
          Are you sure you want to delete the material{" "}
          <strong>
            {materialToDelete.displayName} ({materialToDelete.netWeight} kg)
          </strong>{" "}
          from the sale?
        </p>
        <p>This will permanently delete this material and this action cannot be undone.</p>
      </Modal>
    </div>
  );
};

export default SaleForm;
